// Type-only: `@checkr/unified-login-sdk` is ESM-only (`"type": "module"`, no
// `require` condition on any subpath). The Express server and `scripts/ssg`
// load `src/` as CommonJS through `@babel/register`, which compiles a static
// import to `require()` — so importing the SDK here for its value would throw
// `ERR_PACKAGE_PATH_NOT_EXPORTED` before the server can listen. The value
// import lives in `getUnifiedLoginClient`, where Node never reaches it and
// Vite code-splits it for the browser. Guarded by
// `__tests__/nodeModuleGraph.test.ts`.
import type { UnifiedLoginClient } from '@checkr/unified-login-sdk/client';
import * as Sentry from '@sentry/react';
import { UNIFIED_LOGIN_BROKER_URL } from '../constants';
import { isSessionStorageSupported } from '../lib/helpers';

/** Broker failures are reported, never swallowed, so they stay diagnosable. */
const reportBrokerFailure = (context: string, cause: unknown): void => {
  Sentry.captureException(new Error(`unified-login ${context}`, { cause }));
};

export const ENTITY_NAME = 'candidate-portal';
export const UNIFIED_LOGIN_SESSION_KEY = 'unifiedLoginSession';

// The in-flight construction is cached, not just the finished client: the
// getter is async, so two callers racing the first load (a route bootstrap and
// a 401 interceptor, say) would both see an empty slot and build a second
// broker.
let clientPromise: Promise<UnifiedLoginClient> | null = null;
let inboundFailedThisLoad = false;

export const markInboundExchangeFailed = (): void => {
  inboundFailedThisLoad = true;
};

export const didInboundExchangeFailThisLoad = (): boolean =>
  inboundFailedThisLoad;

/**
 * Whether kickoff has already stood down in this page load.
 *
 * Load-scoped rather than per-hook, because the kickoff hook remounts as the
 * candidate moves around: the US path navigates `/login` → WorkOS, and the SSN
 * fallback links back to `/login?force=ssn`. A fresh instance starts with
 * empty refs, so without this a tab that already chose the legacy form — by
 * fail-open timeout, or because the candidate clicked SSN to stay put — gets
 * sent to the broker by the next mount.
 */
let kickoffStoodDownThisLoad = false;

export const markKickoffStoodDown = (): void => {
  kickoffStoodDownThisLoad = true;
};

export const didKickoffStandDownThisLoad = (): boolean =>
  kickoffStoodDownThisLoad;

const createUnifiedLoginClient = async (
  brokerUrl: string,
): Promise<UnifiedLoginClient> => {
  const { UnifiedLoginClient: Client } = await import(
    '@checkr/unified-login-sdk/client'
  );

  // No `tokenExchange`, deliberately. That option is the SDK's Auth0 SPA
  // browser redeem, and it mints the entity tenant's own access token —
  // which is not the AMS candidate JWT that `candidate-portal-bff` validates
  // as `Authz::CANDIDATE`. The portal already gets that JWT from
  // api-personal-checks (`POST /candidate_portal_auth`, and the WorkOS
  // verify/oauth paths, via `Operations::IssueCandidatePortalJwt`), so a
  // broker assertion has to be redeemed through that same loop, server side.
  // Omitting the option is the SDK's documented server-redeem shape:
  // `getAccessToken()` answers `unavailable` rather than handing the browser
  // a token nothing downstream accepts. See ECDEX-3510.
  return new Client({
    brokerUrl,
    entityName: ENTITY_NAME,
    sessionCheck: 'iframe',
  });
};

/**
 * Resolves the broker client, loading the SDK on first use.
 *
 * Rejects only if the SDK chunk cannot be fetched. Every caller treats that
 * as "no broker" and falls through to the pre-existing Auth0 path.
 */
export const getUnifiedLoginClient =
  async (): Promise<UnifiedLoginClient | null> => {
    if (!UNIFIED_LOGIN_BROKER_URL) return null;

    // A chunk that failed to load can succeed on a later attempt, so the
    // rejected promise is dropped rather than cached as a permanent "no
    // broker" for the rest of the page's life.
    clientPromise ??= createUnifiedLoginClient(UNIFIED_LOGIN_BROKER_URL).catch(
      (error: unknown) => {
        clientPromise = null;
        throw error;
      },
    );

    return clientPromise;
  };

const storageGet = (key: string): string | null =>
  isSessionStorageSupported() ? globalThis.sessionStorage.getItem(key) : null;

const storageSet = (key: string, value: string): void => {
  if (isSessionStorageSupported()) {
    globalThis.sessionStorage.setItem(key, value);
  }
};

const storageRemove = (key: string): void => {
  if (isSessionStorageSupported()) {
    globalThis.sessionStorage.removeItem(key);
  }
};

export const hasUnifiedLoginSession = (): boolean =>
  storageGet(UNIFIED_LOGIN_SESSION_KEY) === 'true';

export const markUnifiedLoginSession = (): void => {
  storageSet(UNIFIED_LOGIN_SESSION_KEY, 'true');
};

export const clearUnifiedLoginSession = (): void => {
  storageRemove(UNIFIED_LOGIN_SESSION_KEY);
};

export const unifiedLoginRedirect = async (): Promise<boolean> => {
  try {
    const brokerClient = await getUnifiedLoginClient();
    if (!brokerClient) return false;
    markUnifiedLoginSession();
    brokerClient.login();
    return true;
  } catch (error) {
    reportBrokerFailure('redirect failed', error);
    return false;
  }
};

export const unifiedLoginLogout = async (): Promise<boolean> => {
  try {
    // Inside the `try` because loading the SDK can now fail, and local logout
    // must never be blocked by a broker problem.
    const brokerClient = await getUnifiedLoginClient();
    if (!brokerClient) {
      clearUnifiedLoginSession();
      return false;
    }
    await brokerClient.logout();
    clearUnifiedLoginSession();
    return true;
  } catch (error) {
    reportBrokerFailure('logout failed', error);
    return false;
  }
};
